import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { useApi } from "../../dashboard/api";
import { MatrixNodes, SlotProgress, levelPrice } from "../../components/dashboard/MatrixNodes";
import { ErrorState, Panel, Skeleton } from "../../components/dashboard/ui";

export const MatrixPage = () => {
    const { data, loading, error } = useApi("matrix");
    const [selected, setSelected] = useState(1);

    useEffect(() => {
        if (data) setSelected(data.currentLevel);
    }, [data]);

    if (loading) {
        return (
            <div className="grid gap-6 lg:grid-cols-12" data-testid="matrix-loading">
                <Skeleton className="h-96 rounded-xl lg:col-span-4" />
                <Skeleton className="h-96 rounded-xl lg:col-span-8" />
            </div>
        );
    }
    if (error || !data) return <ErrorState testId="matrix-error" />;

    const level = data.levels.find((l) => l.level === selected);

    return (
        <div className="grid gap-6 lg:grid-cols-12" data-testid="matrix-page">
            <Panel title="Levels" padded={false} className="lg:col-span-4" testId="matrix-levels-list">
                <ul className="max-h-[520px] overflow-y-auto">
                    {data.levels.map((l) => {
                        const active = l.status === "active";
                        const isSel = l.level === selected;
                        return (
                            <li key={l.level} className="border-b border-white/[0.04] last:border-0">
                                <button
                                    type="button"
                                    onClick={() => setSelected(l.level)}
                                    data-testid={`matrix-level-${l.level}`}
                                    className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors duration-150 ${
                                        isSel ? "bg-white/[0.03]" : "hover:bg-white/[0.015]"
                                    }`}
                                >
                                    <span className={`w-8 text-xs font-extrabold tabular ${isSel ? "text-nx-lime" : "text-nx-sub"}`}>
                                        L{l.level}
                                    </span>
                                    <span className="text-[13px] font-bold text-nx-text tabular">{levelPrice(l)}</span>
                                    <span className="ms-auto flex items-center gap-1.5">
                                        {active ? (
                                            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-nx-lime">
                                                <span className="h-1.5 w-1.5 rounded-full bg-nx-lime" aria-hidden="true" />
                                                Active
                                            </span>
                                        ) : (
                                            <Lock size={11} strokeWidth={1.5} className="text-nx-mute/70" aria-label="Locked" />
                                        )}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </Panel>

            <Panel className="lg:col-span-8" testId="matrix-detail">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-baseline gap-3">
                        <h2 className="text-2xl font-extrabold tracking-tight text-nx-text tabular" data-testid="matrix-detail-level">
                            LEVEL {level.level}
                        </h2>
                        <span className="text-lg font-extrabold text-nx-sub tabular">{levelPrice(level)}</span>
                    </div>
                    <span
                        className={`rounded-md border px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] ${
                            level.status === "active"
                                ? "border-nx-lime/30 bg-nx-lime/10 text-nx-lime"
                                : "border-white/10 bg-nx-soft text-nx-mute"
                        }`}
                        data-testid="matrix-detail-status"
                    >
                        {level.status === "active" ? "Active" : "Locked"}
                    </span>
                </div>

                <div className="mt-6 max-w-sm">
                    <SlotProgress filled={level.slotsFilled} total={level.slotsTotal} />
                </div>

                <div className="mt-8 rounded-lg border border-white/5 bg-nx-base/60 p-6 sm:p-8">
                    <MatrixNodes slotsFilled={level.slotsFilled} size="lg" />
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-4 text-[10px] font-bold uppercase tracking-[0.14em] text-nx-mute">
                    <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-sm border border-nx-lime/50 bg-nx-lime/15" aria-hidden="true" /> Filled
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-sm border border-white/10 bg-nx-soft" aria-hidden="true" /> Empty
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-sm border border-dashed border-white/15 bg-nx-soft" aria-hidden="true" /> Recycle
                    </span>
                </div>

                <p className="mt-5 text-xs leading-relaxed text-nx-mute" data-testid="matrix-detail-note">
                    {level.status === "active"
                        ? "This is your current active matrix. Filled slots appear here as your matrix positions complete."
                        : "This level is locked. Activate the preceding level to unlock it."}
                </p>
            </Panel>
        </div>
    );
};
